import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, MailCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-base text-white flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <Activity className="w-6 h-6 text-signal" strokeWidth={2.5} />
          <span className="font-heading font-black text-xl tracking-tight">SNETCH</span>
        </Link>

        {sent ? (
          <div data-testid="forgot-sent" className="text-center">
            <MailCheck className="w-14 h-14 text-profit mx-auto mb-6" />
            <h1 className="font-heading font-black text-2xl mb-2">{t("forgot_title")}</h1>
            <p className="font-mono text-sm text-white/50 mb-8">{t("forgot_sent")}</p>
            <Link to="/auth">
              <Button data-testid="back-login-btn" className="bg-signal text-black hover:bg-white rounded-sm font-semibold">
                {t("back_to_login")}
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading font-black text-3xl tracking-tight mb-2">{t("forgot_title")}</h1>
            <div className="w-12 h-1 bg-signal mb-6" />
            <p className="font-mono text-sm text-white/50 mb-6">{t("forgot_desc")}</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">{t("email")}</Label>
                <Input
                  data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="mt-1.5 bg-panel border-white/10 text-white focus-visible:ring-signal rounded-sm"
                />
              </div>
              {error && <p data-testid="forgot-error" className="text-loss text-sm font-mono">{error}</p>}
              <Button
                data-testid="forgot-submit" type="submit" disabled={loading}
                className="w-full bg-signal text-black hover:bg-white rounded-sm font-semibold h-11"
              >
                {loading ? "…" : t("forgot_btn")}
              </Button>
            </form>
            <Link to="/auth" className="mt-6 inline-block text-sm text-white/50 hover:text-signal transition-colors font-mono">
              {t("back_to_login")}
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
